/**
 * Very Video-bureaus (Full Frame Exclusive) — welke klanten reclamebureaus zijn en of ze een
 * zusje (ONLY-groep) of een los bureau zijn. Voedt het Very Video-blok en de branche
 * 'Reclamebureau' in de Opdracht-mix. Opslag: instelling `exclusive_bureaus` als tekst
 * "Naam (zusje), Naam (los), …" — matching gebeurt op de (kale) bedrijfsnaam in Gripp.
 */
import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, IconButton, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import toast from 'react-hot-toast';
import { useSettings } from '../adminSettingsCommon';

const KEY = 'exclusive_bureaus';

export function parseBureaus(raw) {
  return String(raw || '').split(',').map((d) => d.trim()).filter(Boolean).map((d) => {
    const m = d.match(/^(.*?)\s*\((zusje|los)\)\s*$/i);
    return { naam: (m ? m[1] : d).trim(), zusje: !!(m && m[2].toLowerCase() === 'zusje') };
  }).filter((b) => b.naam);
}

export function serialiseerBureaus(rows) {
  return rows.filter((b) => b.naam.trim()).map((b) => `${b.naam.trim()} (${b.zusje ? 'zusje' : 'los'})`).join(', ');
}

export default function VeryVideoBureaus() {
  const { values, loading, saving, save } = useSettings();
  const [rows, setRows] = useState([]);
  const [nieuw, setNieuw] = useState('');
  const [nieuwZusje, setNieuwZusje] = useState(true);
  const opgeslagen = values[KEY];
  useEffect(() => { setRows(parseBureaus(opgeslagen)); }, [opgeslagen]);
  const dirty = useMemo(() => serialiseerBureaus(rows) !== serialiseerBureaus(parseBureaus(opgeslagen)), [rows, opgeslagen]);
  const zusjes = rows.filter((r) => r.zusje);
  const losse = rows.filter((r) => !r.zusje);

  const voegToe = () => {
    const naam = nieuw.trim();
    if (!naam) return;
    if (rows.some((r) => r.naam.toLowerCase() === naam.toLowerCase())) { toast('Staat er al.'); return; }
    setRows((p) => [...p, { naam, zusje: nieuwZusje }]);
    setNieuw('');
  };
  const bewaar = async () => {
    await save(KEY, serialiseerBureaus(rows));
    toast.success('Bureaus opgeslagen — de Opdracht-mix gebruikt de nieuwe lijst direct; de branche-indeling volgt bij de volgende run of via “Branches indelen”.');
  };

  const Lijst = ({ titel, items, kleur }) => (
    <Box sx={{ flex: 1, minWidth: 260 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', mb: 1 }}>{titel} · {items.length}</Typography>
      <Stack spacing={0.5}>
        {items.map((b) => (
          <Stack key={b.naam} direction="row" alignItems="center" spacing={1} sx={{ px: 1.25, py: 0.5, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
            <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>{b.naam}</Typography>
            <Tooltip title={b.zusje ? 'Maak los bureau' : 'Maak zusje (ONLY)'}>
              <Chip size="small" label={b.zusje ? 'zusje' : 'los'} onClick={() => setRows((p) => p.map((r) => (r.naam === b.naam ? { ...r, zusje: !r.zusje } : r)))}
                sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: kleur.bg, color: kleur.fg, cursor: 'pointer' }} />
            </Tooltip>
            <IconButton size="small" onClick={() => setRows((p) => p.filter((r) => r.naam !== b.naam))}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton>
          </Stack>
        ))}
        {items.length === 0 && <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Geen</Typography>}
      </Stack>
    </Box>
  );

  return (
    <Box>
      <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 0.5 }}>🎬 Very Video-bureaus (Full Frame Exclusive)</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
        Reclamebureaus waarvoor we whitelabel werken. <b>Zusje</b> = onderdeel van de ONLY-groep, <b>los</b> = zelfstandig bureau.
        Een klant telt mee als de naam hier (zonder spaties en leestekens) in de Gripp-bedrijfsnaam voorkomt; “Schrijf-Schrijf” vangt dus ook
        “Schrijf-Schrijf bureau voor contentcreatie”. Klik op zusje/los om te wisselen.
      </Typography>
      {loading ? <Typography>Laden…</Typography> : (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <Lijst titel="Zusjes (ONLY)" items={zusjes} kleur={{ bg: '#fdf2f8', fg: '#9d174d' }} />
            <Lijst titel="Losse bureaus" items={losse} kleur={{ bg: '#f1f5f9', fg: '#475569' }} />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <TextField size="small" label="Bureau toevoegen (naam zoals in Gripp)" value={nieuw} onChange={(e) => setNieuw(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') voegToe(); }} sx={{ minWidth: 300 }} />
            <ToggleButtonGroup size="small" exclusive value={nieuwZusje ? 'zusje' : 'los'} onChange={(_e, v) => v && setNieuwZusje(v === 'zusje')}
              sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25, fontSize: 12, textTransform: 'none', fontWeight: 700 } }}>
              <ToggleButton value="zusje">zusje</ToggleButton>
              <ToggleButton value="los">los</ToggleButton>
            </ToggleButtonGroup>
            <Button size="small" variant="outlined" onClick={voegToe} sx={{ textTransform: 'none', fontWeight: 700 }}>Toevoegen</Button>
          </Stack>
          {dirty && <Alert severity="info" sx={{ mb: 1.5 }}>Nog niet opgeslagen.</Alert>}
          <Button variant="contained" disableElevation disabled={!dirty || !!saving[KEY]} onClick={bewaar} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {saving[KEY] ? 'Opslaan…' : 'Opslaan'}
          </Button>
        </>
      )}
    </Box>
  );
}
